<script lang="ts">
    import Vue from "vue";
    import Component, { mixins } from 'vue-class-component';
    import { EditorState, generateName } from "@common/Common";
    import { PortfolioScenarioSettingsReadModel, WeightsSetReadModel } from "@Quest/PortfolioScenarios/Domain/readmodels";
    import PortfolioScenariosQueries from "@Quest/PortfolioScenarios/Domain/queries-vue-component";
    import PortfolioScenariosCommands from "@Quest/PortfolioScenarios/Domain/commands-vue-component";
    import { guid } from "@common/Guid";
    import { UpdatePortfolioScenarioCommandV1 } from "@Quest/PortfolioScenarios/Domain/commands-core";
    import { ICommandBase } from "@Quest/cqrs";
    import { PortfolioScenarioEditorViewModel } from "@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor";


    @Component({
        props: {
            viewModel: { type: Object, required: true }
        },
        watch: {
            opened: 'onOpenedChanged',
            criteriaHierarchyId: 'onCriteriaHierarchyIdChanged'
        }
    })
    export default class PortfolioScenarioEditor extends mixins(PortfolioScenariosQueries, PortfolioScenariosCommands)
    {
        get vm(): PortfolioScenarioEditorViewModel { return this.$props.viewModel as PortfolioScenarioEditorViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        rm: PortfolioScenarioSettingsReadModel = null;      
        criteriaHierarchyId: number | null = null;
        weightsSets: WeightsSetReadModel[] = null;
        weightsSetId: number | null = null;

        get workspaceId() { return this.vm.workspaceId; }
        get portfolioId() { return this.vm.portfolioId; }
        get portfolioScenarioId() { return this.vm.portfolioScenarioId; }

        get corporateScenarioId(): string { return this.vm.corporateScenarioId; }
        set corporateScenarioId(val: string) { this.vm.corporateScenarioId = val; }
        

        //@Watch('opened')
        onOpenedChanged(val: boolean, oldVal: boolean)
        {
            if (val === true)
            {
                this.rm = null;

                if (this.vm.state === EditorState.Creator)
                {
                    this.runNewScenarioSettingsQueryV1(this.workspaceId, this.portfolioId, this.corporateScenarioId);
                }
                else if (this.vm.state === EditorState.Editor)
                {
                    this.runExistingScenarioSettingsQueryV1(this.workspaceId, this.portfolioId, this.portfolioScenarioId);
                }
            }
            else
            {
                this.rm = null;
            }
        }

        //@Watch('criteriaHierarchyId')
        onCriteriaHierarchyIdChanged(val: number | null, oldVal: number | null)
        {
            this.weightsSets = this.rm.ValueDefinitions.find(a => a.Id === this.criteriaHierarchyId)?.WeightsSets ?? [];

            if (this.weightsSetId)
            {
                if (this.weightsSets.some(a => a.Id === this.weightsSetId) === false)
                {
                    this.weightsSetId = null;
                }
            }
        }

        onFormSubmit()
        {
            if (this.vm.state === EditorState.Creator)
            {
                this.invokeCreatePortfolioScenarioCommandV1(this.workspaceId, this.corporateScenarioId, this.portfolioId, guid(), this.rm.Name, this.rm.Description,
                    this.criteriaHierarchyId, this.weightsSetId, this.rm.ApplyBenefitsConfidenceFactor,
                    this.rm.Denomination, this.rm.ApprovalAcceptedStatusId, this.rm.ApprovalRejectedStatusId);
            }
            else if (this.vm.state === EditorState.Editor)
            {
                this.invokeUpdatePortfolioScenarioCommandV1(this.workspaceId, this.corporateScenarioId, this.portfolioId, this.rm.Id, this.rm.Name, this.rm.Description,
                    this.criteriaHierarchyId, this.weightsSetId, this.rm.ApplyBenefitsConfidenceFactor,
                    this.rm.Denomination, this.rm.ApprovalAcceptedStatusId, this.rm.ApprovalRejectedStatusId);
            }
        }

        close()
        {
            this.vm.close();
        }

        onSetModel(model: any, modelName: string) 
        {
            if (modelName == "PortfolioScenarioSettingsReadModel")
            {
                const rm = model as PortfolioScenarioSettingsReadModel;

                if (this.vm.state === EditorState.Cloner)
                {
                    rm.Name = generateName(this.rm.Name);
                }

                this.rm = rm;
                this.criteriaHierarchyId = this.rm.CriteriaHierarchyId;
                this.weightsSetId = this.rm.WeightsSetId;
                this.corporateScenarioId = this.rm.CorporateScenarioId;
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string)
        {
            if (UpdatePortfolioScenarioCommandV1.matchType(cmd))
            {
                this.close();
            }
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ vm.modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <form v-if="rm" @submit.prevent="onFormSubmit" id="settingsForm">
                            <div class="form-row">
                                <div class="form-group col">
                                    <label>Plan name:</label>
                                    <input type="text" v-model.trim="rm.Name" class="form-control" required minlength="2" />
                                </div>
                                <div class="form-group col">
                                    <label>Link to Enterprise Plan:</label>
                                    <select class="form-control" v-model="corporateScenarioId" :disabled="rm.IsActive">
                                        <option :value="null" disabled>Choose...</option>
                                        <option v-for="cs in rm.CorporateScenarios" :value="cs.Id">
                                            {{ cs.Name }}
                                        </option>
                                    </select>
                                    <small class="form-text text-muted" v-if="rm.IsActive">
                                        This cannot be changed because the plan is currently active in that enterprise plan.
                                    </small>
                                </div>

                            </div>

                            <div class="form-group">
                                <label>Description:</label>
                                <textarea v-model.trim="rm.Description" class="form-control" />
                            </div>

                            <div class="form-row">
                                <div class="form-group col">
                                    <label for="denomination">Currency denomination:</label>
                                    <select id="denomination" v-model="rm.Denomination" class="form-control">
                                        <option value="Unit">Unit</option>
                                        <option value="Thousand">Thousand</option>
                                        <option value="Million">Million</option>
                                    </select>
                                </div>

                                <div class="form-group col">
                                    <label>Risk adjusted benefits:</label>
                                    <select class="form-control" v-model="rm.ApplyBenefitsConfidenceFactor">
                                        <option :value="true">Enabled</option>
                                        <option :value="false">Disabled</option>
                                    </select>
                                </div>
                            </div>


                            <fieldset>
                                <legend>Value definition</legend>

                                <div class="form-group">
                                    <label>Goal:</label>
                                    <select class="form-control" v-model="criteriaHierarchyId">
                                        <option :value="null">Choose...</option>
                                        <option v-for="ch in rm.ValueDefinitions" :value="ch.Id">{{ch.Name}}</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <label>Weights set:</label>
                                    <select class="form-control" v-model="weightsSetId">
                                        <option :value="null">Default</option>
                                        <option v-for="ws in weightsSets" :value="ws.Id">{{ws.Name}}</option>
                                    </select>
                                </div>
                            </fieldset>


                            <fieldset>
                                <legend>Approval</legend>

                                <div class="form-group">
                                    <label>Accepted:</label>
                                    <select class="form-control" v-model="rm.ApprovalAcceptedStatusId">
                                        <option :value="null">Do not change</option>
                                        <option v-for="s in rm.Statuses" :value="s.Id">{{s.Name}}</option>
                                    </select>
                                    <small class="form-text text-muted">
                                        Kanban status for Accepted projects. The Kanban status will be updated when you Approve your decision in the scenario.
                                    </small>
                                </div>
                                <div class="form-group">
                                    <label>Rejected:</label>
                                    <select class="form-control" v-model="rm.ApprovalRejectedStatusId">
                                        <option :value="null">Do not change</option>
                                        <option v-for="s in rm.Statuses" :value="s.Id">{{s.Name}}</option>
                                    </select>
                                    <small class="form-text text-muted">
                                        Kanban status for Rejected projects. The Kanban status will be updated when you Approve your decision in the scenario.
                                    </small>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="settingsForm" class="btn btn-primary">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
